import { BLOG_COURSES, type BlogCourseId } from '../../../constants/BlogCourses';

export const BLOG_COURSE_PROGRESS_STORAGE_KEY = 'blog_course_progress';

// コースを飛ばして途中の記事から読んだ場合に未読記事まで読了扱いにしないよう、
// 到達ステップ数ではなく実際に読んだ記事の slug 一覧を保持する。
export type CourseProgress = Partial<Record<BlogCourseId, string[]>>;

export type MarkStepAsReadResult = {
  progress: CourseProgress;
  wasMarkedAsRead: boolean;
};

// browserslist（`>0.2%, not dead`）を考慮し、Object.hasOwn より互換性の高いAPIを使う
export const isBlogCourseId = (value: string): value is BlogCourseId =>
  Object.getOwnPropertyDescriptor(BLOG_COURSES, value) !== undefined;

const persistCourseProgress = (progress: CourseProgress) => {
  try {
    window.localStorage.setItem(BLOG_COURSE_PROGRESS_STORAGE_KEY, JSON.stringify(progress));
  } catch {
    // プライベートブラウジング等で storage が使えない環境では進捗保存をあきらめて表示のみ行う
  }
};

export const readCourseProgress = (): CourseProgress => {
  try {
    const raw = window.localStorage.getItem(BLOG_COURSE_PROGRESS_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      persistCourseProgress({});
      return {};
    }
    const progress: CourseProgress = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (isBlogCourseId(key) && Array.isArray(value)) {
        const validSlugs = new Set(BLOG_COURSES[key].steps.map((step) => step.slug));
        const readSlugs = [...new Set(value)].filter(
          (slug): slug is string => typeof slug === 'string' && validSlugs.has(slug)
        );
        if (readSlugs.length > 0) {
          progress[key] = readSlugs;
        }
      }
    }
    if (JSON.stringify(progress) !== raw) {
      persistCourseProgress(progress);
    }
    return progress;
  } catch {
    persistCourseProgress({});
    return {};
  }
};

export const writeCourseProgress = (progress: CourseProgress) => {
  persistCourseProgress(progress);
};

// 現在表示中の記事だけを既読としてマークする（コースを飛ばして読んでも未読記事は読了にしない）
export const markStepAsRead = (courseId: BlogCourseId, slug: string): MarkStepAsReadResult => {
  const progress = readCourseProgress();
  const readSlugs = progress[courseId] ?? [];
  if (readSlugs.includes(slug)) {
    return { progress, wasMarkedAsRead: false };
  }
  const nextProgress: CourseProgress = { ...progress, [courseId]: [...readSlugs, slug] };
  writeCourseProgress(nextProgress);
  return { progress: nextProgress, wasMarkedAsRead: true };
};
